import { useQuery } from "@apollo/client";
import { useMemo } from "react";
import { SPEND_BY_REVENUE_RANGE_QUERY } from "../../../../../legacy/queries/MI";
import { sanitizeCompanyFilters } from "../../../../../legacy/utils/companyFilterUtils";
import { COMPANY_SEARCH_CLIENT } from "../../../../../utils/consts";
import { CompanyFilter } from "../../../../../legacy/types/companyFilter";
import {
  millionsFrom,
  toCompactCurrencyRange,
} from "../../../../../utils/numberUtils";
import { MinMaxValue, MinMaxOption } from "../../../../../types/filters";
import { useWorkspaceStore } from "../../../../../store/workspace/WorkspaceStore";
import type {
  ModelScoreInput,
  ModelScoreFilterInput,
} from "../../../../../generated/company-search";

const MI_REVENUE_RANGES: MinMaxOption[] = millionsFrom([
  1,
  10,
  50,
  100,
  200,
  500,
  1000,
  5000,
  Number.POSITIVE_INFINITY,
]).map(toCompactCurrencyRange);

type RevenueBreakdownQueryPayload = {
  segmentAnalysisRevenueRangeBreakdown: {
    companyCount: number;
    totalSpend: number;
    range: MinMaxValue;
  }[];
};

interface Props {
  companyFilters: CompanyFilter;
  customSpendCategory?: {
    name: string;
    categories: Array<{ id: string; weight: number }>;
  };
  localizeInstalls?: boolean;
  /** V2 ("model tier") scoring narrowing — mirrors opportunityGeneratorSearch's
   * args. */
  tenantId?: string;
  modelScores?: ModelScoreInput[];
  modelScoreFilter?: ModelScoreFilterInput[];
  /** Share-page panel tag (Apollo context); see toPanelDescriptor. */
  activeModelId?: string;
  /** Skips the query entirely. Omitted (undefined) behaves exactly like
   * `false` — existing callers that never pass this see no change. */
  skip?: boolean;
}

export function useSpendByRevenueRangeData(props: Props) {
  const workspaceWhitespace = useWorkspaceStore(
    (state) => state.draft?.configuration?.whitespace
  );

  const {
    loading: isLoading,
    data,
    error,
  } = useQuery<RevenueBreakdownQueryPayload>(SPEND_BY_REVENUE_RANGE_QUERY, {
    variables: {
      opportunityGeneratorFilters: sanitizeCompanyFilters(
        props.companyFilters,
        true
      ),
      localizeInstalls: props.localizeInstalls,
      customSpendCategory: props.customSpendCategory,
      companyMatchFile:
        props.companyFilters?.opportunityType &&
        workspaceWhitespace?.fileId &&
        workspaceWhitespace?.lastMatched
          ? workspaceWhitespace
          : undefined,
      tenantId: props.tenantId,
      modelScores: props.modelScores,
      modelScoreFilter: props.modelScoreFilter,
    },
    context: {
      clientName: COMPANY_SEARCH_CLIENT,
      activeModelId: props.activeModelId,
    },
    skip: props.skip,
  });

  const spendBreakdownResults = useMemo(() => {
    return (
      data?.segmentAnalysisRevenueRangeBreakdown
        ?.map(({ companyCount, totalSpend, range }) => ({
          companies: companyCount,
          spend: totalSpend,
          dimension:
            MI_REVENUE_RANGES.find(
              ({ value }) => value.max === range.max || value.min === range.min
            )?.label || "unknown",
        }))
        .reverse() || []
    );
  }, [data]);

  return {
    isLoading,
    error,
    spendBreakdownResults,
  };
}
